import { useEffect, useMemo, useState } from "react";
import apiClient from "@shared/services/apiClient";
import logger from "@shared/utils/logger";
import {
  LOCK_POLICY_DEFAULTS,
  sanitizeInactivityTimeoutMinutes,
  type LockPolicy,
} from "@shared/config/lockPolicy";
import { useAuth } from "@shared/context/AuthContext";

interface LockPolicyPayload {
  inactivityTimeoutMinutes?: number;
  policyVersion?: string;
}

interface UseLockPolicyResult {
  policy: LockPolicy;
  loading: boolean;
}

export function useLockPolicy(): UseLockPolicyResult {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [policy, setPolicy] = useState<LockPolicy>({
    inactivityTimeoutMinutes: LOCK_POLICY_DEFAULTS.inactivityTimeoutMinutes,
    policyVersion: LOCK_POLICY_DEFAULTS.policyVersion,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchPolicy = async () => {
      try {
        if (authLoading) {
          return;
        }

        if (!isAuthenticated) {
          if (!cancelled) {
            setLoading(false);
          }
          return;
        }

        const response = await apiClient.get("/users/unlock-policy");
        const payload: LockPolicyPayload = response.data?.data ?? response.data;

        if (!cancelled) {
          setPolicy({
            inactivityTimeoutMinutes: sanitizeInactivityTimeoutMinutes(
              payload.inactivityTimeoutMinutes,
            ),
            policyVersion:
              payload.policyVersion || LOCK_POLICY_DEFAULTS.policyVersion,
          });
        }
      } catch (error) {
        logger.warn("Failed to fetch lock policy; using default settings", error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void fetchPolicy();

    return () => {
      cancelled = true;
    };
  }, [authLoading, isAuthenticated]);

  return useMemo(
    () => ({
      policy,
      loading,
    }),
    [loading, policy],
  );
}
